import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { DspRejectedAdService, DspService } from "@front/m19-services";
import { IAlertComponent, IButtonDirective, IIconComponent, ModalService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { catchError, combineLatest, map, of, switchMap } from "rxjs";
import {
  DspRejectedAdsModalComponent,
  DspRejectedAdsModalData,
  DspRejectedVerdictView,
  toRejectedVerdicts,
} from "./dsp-rejected-ads-modal.component";

@Component({
  selector: "app-dsp-rejected-ads-badge",
  imports: [IAlertComponent, IButtonDirective, IIconComponent, TranslocoDirective],
  template: `
    @if (moderatedAdCount() > 0) {
      <ng-container *transloco="let t">
        <IAlert type="warning" [withIcon]="false">
          <div class="flex items-center gap-3">
            <IIcon name="alertCircle" />
            <span class="type-body-medium">
              {{ t("dsp-creatives.n_moderated_ads", { count: moderatedAdCount() }) }}
            </span>
            <button iButton variant="secondary" size="sm" (click)="openModerationModal()">
              {{ t("common.view_details") }}
            </button>
          </div>
        </IAlert>
      </ng-container>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspRejectedAdsBadgeComponent {
  private readonly dspRejectedAdService = inject(DspRejectedAdService);
  private readonly dspService = inject(DspService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);

  readonly dspAdvertiserId = input.required<string>();
  readonly strategyId = input.required<number>();

  private readonly verdicts = toSignal(
    combineLatest([toObservable(this.dspAdvertiserId), toObservable(this.strategyId)]).pipe(
      switchMap(([dspAdvertiserId, strategyId]) =>
        combineLatest([
          this.dspRejectedAdService.getDspRejectedAds(dspAdvertiserId),
          this.dspService.getDspAdgroups(dspAdvertiserId),
          this.dspService.getDspCreatives(dspAdvertiserId),
        ]).pipe(
          map(([rejectedAds, adGroups, creatives]) => toRejectedVerdicts(rejectedAds, strategyId, adGroups, creatives)),
          // a moderation message is informational: failing to load it must not break the creatives tab
          catchError(() => of(<DspRejectedVerdictView[]>[])),
        ),
      ),
    ),
    { initialValue: [] },
  );

  readonly moderatedAdCount = computed(() => this.verdicts().reduce((count, verdict) => count + verdict.ads.length, 0));

  openModerationModal(): void {
    this.modalService.openModal<DspRejectedAdsModalData, void>(DspRejectedAdsModalComponent, {
      modalTitle: this.translocoService.translate("dsp-creatives.dsp_moderation_messages"),
      size: "md",
      data: { verdicts: this.verdicts() },
    });
  }
}
